/**
 * SoleadLab 2.0 — Centralized API Fetch
 *
 * Single fetch wrapper for all API calls.
 * Reads auth token from authStore, injects Authorization + X-Org-Id headers.
 * On 401, silently exchanges the httpOnly refresh cookie for a new access
 * token and retries the original request once. Also runs a proactive timer
 * that refreshes the token shortly before it would expire.
 */

import { useAuthStore } from '../stores/authStore';
import type { AuthUser, OrgSummary } from '../stores/authStore';
import { useShareSessionStore } from '../stores/shareSessionStore';
import { getFieldActor, FIELD_ACTOR_HEADER } from '../utils/fieldActor';

export const API_BASE = import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_API_BASE_URL || 'https://api.soleadlab.com');

const DEFAULT_TIMEOUT = 30_000;

/**
 * Refresh ~2 minutes before the 15-minute access token would expire,
 * leaving plenty of slack for clock skew and slow networks.
 */
const PROACTIVE_REFRESH_MS = 13 * 60 * 1000;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Build auth headers from the current auth state.
 * Used by callers that need raw headers (e.g., FormData uploads).
 */
export function getAuthHeaders(): Record<string, string> {
  const { token, currentOrg } = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (currentOrg) headers['X-Org-Id'] = currentOrg.id;
  return headers;
}

// ── Silent refresh ──────────────────────────────────────────────────────────

let inFlightRefresh: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  // Coalesce concurrent 401s into a single refresh round-trip.
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v2/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        useAuthStore.getState().logout();
        return null;
      }

      const body = await response.json().catch(() => null) as {
        data?: {
          token?: string;
          user?: AuthUser;
          currentOrg?: OrgSummary | null;
          orgs?: OrgSummary[];
        };
      } | null;
      const data = body?.data;

      if (!data?.token || !data.user) {
        useAuthStore.getState().logout();
        return null;
      }

      useAuthStore
        .getState()
        .login(data.token, data.user, data.currentOrg ?? null, data.orgs ?? []);
      return data.token;
    } catch {
      useAuthStore.getState().logout();
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

// ── Proactive refresh timer ────────────────────────────────────────────────

let proactiveTimer: ReturnType<typeof setTimeout> | null = null;

function cancelProactiveRefresh() {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

function scheduleProactiveRefresh() {
  cancelProactiveRefresh();
  proactiveTimer = setTimeout(() => {
    proactiveTimer = null;
    // Fire and forget — failures call logout() inside attemptRefresh.
    void attemptRefresh();
  }, PROACTIVE_REFRESH_MS);
}

// Re-arm whenever the token changes (login, manual refresh, switch org).
let lastToken = useAuthStore.getState().token;
if (lastToken) scheduleProactiveRefresh();

useAuthStore.subscribe((state) => {
  if (state.token === lastToken) return;
  lastToken = state.token;
  if (state.token) scheduleProactiveRefresh();
  else cancelProactiveRefresh();
});

// ── Main fetch wrapper ─────────────────────────────────────────────────────

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<T> {
  return apiFetchInternal<T>(path, options, timeout, false);
}

/**
 * Like {@link apiFetch} but returns the raw {@link Response} so callers can
 * read response headers (ETag, Location, etc.) and inspect status codes
 * (e.g. 409 conflict) themselves. Still applies auth headers, credentials,
 * and the silent 401 → refresh → retry flow.
 *
 * The response body is *not* consumed — callers must call `.json()` /
 * `.text()` / `.arrayBuffer()` themselves.
 */
export async function apiFetchRaw(
  path: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<Response> {
  return apiFetchRawInternal(path, options, timeout, false);
}

/**
 * Like {@link apiFetch} but also returns the integer parsed from the ETag
 * response header (used by entity endpoints for optimistic-concurrency
 * `_version` tracking). Returns 0 if no ETag was present.
 *
 * Throws {@link ApiError} on non-2xx, with `.status === 409` for version
 * conflicts — callers can use `isConflictError(err)` to branch.
 */
export async function apiFetchWithEtag<T>(
  path: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<{ data: T; etag: number }> {
  const response = await apiFetchRaw(path, options, timeout);
  const body = (await response.json().catch(() => null)) as
    | { data?: T; error?: { code?: string; message?: string; details?: unknown } }
    | null;

  if (!response.ok) {
    throw new ApiError(
      body?.error?.message || `HTTP ${response.status}`,
      response.status,
      body?.error?.code || 'UNKNOWN',
      body?.error?.details
    );
  }

  const etag = parseInt(response.headers.get('ETag') || '0', 10);
  const data = (body?.data ?? body) as T;
  return { data, etag: isNaN(etag) ? 0 : etag };
}

/**
 * True if `err` represents a 409 version conflict. Accepts any thrown shape
 * that exposes a numeric `.status === 409` (covers our own {@link ApiError}
 * as well as plain `Error` objects with a tacked-on status field).
 */
export function isConflictError(err: unknown): boolean {
  return err instanceof Error && (err as Error & { status?: number }).status === 409;
}

/**
 * User-initiated logout: revoke the server-side refresh token (so the cookie
 * stops working) and clear local auth state. Network failures are non-fatal —
 * we always tear down local state in the finally block.
 */
export async function serverLogout(): Promise<void> {
  try {
    await apiFetch('/api/v2/auth/logout', { method: 'POST' });
  } catch {
    // Already-expired or revoked tokens 401 here — local cleanup is what matters.
  } finally {
    useAuthStore.getState().logout();
  }
}

/**
 * Sign out of every active session for this user (revokes all refresh-token
 * families). Useful for "I lost my laptop" scenarios.
 */
export async function serverLogoutAll(): Promise<void> {
  try {
    await apiFetch('/api/v2/auth/logout-all', { method: 'POST' });
  } catch {
    // ignore — local cleanup is the floor
  } finally {
    useAuthStore.getState().logout();
  }
}

/**
 * Shared transport: builds headers (auth + org + credentials), runs the
 * fetch, and handles the 401 → silent-refresh → retry-once dance. Returns the
 * raw Response. Used by both {@link apiFetch} and {@link apiFetchRaw}.
 */
async function apiFetchRawInternal(
  path: string,
  options: RequestInit,
  timeout: number,
  isRetry: boolean
): Promise<Response> {
  const { token, currentOrg } = useAuthStore.getState();
  const { active: shareActive } = useShareSessionStore.getState();

  // F-02D: share-mode is OPT-IN. We only enter share-mode when:
  //   (1) the visitor has no Bearer token (anonymous), AND
  //   (2) shareSessionStore explicitly says `active: true` (they completed
  //       resolve/unlock on /share/:token in THIS tab).
  // Authenticated users with a stray sl_share cookie keep their normal
  // Bearer auth path (and may still hit the backend's share branch on
  // shareReadable routes — see the F-02C-2 commit note on that edge case).
  const useShareMode = shareActive && !token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (!useShareMode) {
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (currentOrg) headers['X-Org-Id'] = currentOrg.id;
  } else {
    // Share-mode: no Authorization, no X-Org-Id. The sl_share cookie is the
    // credential; `credentials: 'include'` (below, unchanged) carries it.
    // FL-1: field-link writes additionally carry the technician's
    // self-reported name — the backend 400s without it, and the custom
    // header doubles as CSRF hardening (forces a CORS preflight).
    const method = (options.method ?? 'GET').toUpperCase();
    const { view } = useShareSessionStore.getState();
    if (method !== 'GET' && view?.kind === 'field') {
      const actor = getFieldActor(view.id);
      // Header values must be ByteString (≤ U+00FF) — a raw Hebrew/Unicode
      // name would make fetch() throw. Percent-encode; the backend decodes.
      if (actor) headers[FIELD_ACTOR_HEADER] = encodeURIComponent(actor);
    }
  }

  // FormData: let the browser set Content-Type with multipart boundary.
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const response = await fetch(url, {
    ...options,
    headers,
    // Required so the httpOnly refresh cookie ships on /api/v2/auth/* calls
    // AND so the httpOnly sl_share cookie ships on share-readable calls.
    // Path scoping on each cookie itself prevents leakage to other endpoints.
    credentials: 'include',
    signal: controller.signal,
  });

  clearTimeout(timeoutId);

  if (response.status === 401) {
    // /auth/login 401 means wrong password / unknown email — that's a fresh
    // attempt, not an expired session. Pass the response through so the
    // body-parsing layer can surface the real "Invalid email or password"
    // message; otherwise the user sees a misleading "Session expired" on
    // every wrong-password attempt.
    if (path.startsWith('/api/v2/auth/login')) {
      return response;
    }

    // F-02D: share-mode 401 has nothing to refresh. The sl_refresh cookie
    // isn't the credential for shareReadable routes, and there's no auth
    // store to roll. Surface a clean error so SharePage / the consuming
    // viewer can redirect back to /share/:token for a fresh handshake.
    if (useShareMode) {
      throw new ApiError(
        'Share session expired or invalid',
        401,
        'SHARE_SESSION_EXPIRED',
      );
    }

    // Don't attempt to refresh inside the refresh/logout flow itself,
    // and don't retry more than once.
    const isAuthEndpoint =
      path.startsWith('/api/v2/auth/refresh') ||
      path.startsWith('/api/v2/auth/logout');

    if (isRetry || isAuthEndpoint) {
      useAuthStore.getState().logout();
      throw new ApiError('Session expired', 401, 'UNAUTHORIZED');
    }

    const newToken = await attemptRefresh();
    if (!newToken) {
      throw new ApiError('Session expired', 401, 'UNAUTHORIZED');
    }

    // Retry once with the freshly-rotated access token. apiFetchRawInternal
    // re-reads the store on entry, so it picks up the new token automatically.
    return apiFetchRawInternal(path, options, timeout, true);
  }

  return response;
}

async function apiFetchInternal<T>(
  path: string,
  options: RequestInit,
  timeout: number,
  _isRetry: boolean
): Promise<T> {
  try {
    const response = await apiFetchRawInternal(path, options, timeout, false);

    // Non-JSON responses (binary downloads, etc.). Fall back to JSON when no
    // Content-Type is set — production worker always sets it; this is mostly a
    // defensive guard for tests that mock fetch with bare-minimum responses.
    const contentType = response.headers?.get?.('Content-Type') ?? 'application/json';
    if (!contentType.includes('application/json')) {
      if (!response.ok) {
        throw new ApiError(`HTTP ${response.status}`, response.status, 'NON_JSON_ERROR');
      }
      return response as unknown as T;
    }

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const err = body as { error?: { code?: string; message?: string; details?: unknown } } | null;
      const code = err?.error?.code || 'UNKNOWN';
      const message = err?.error?.message || `HTTP ${response.status}`;

      // Phase 3 slice 1: surface PLAN_LIMIT 403s through the billing store so
      // the global UpgradePrompt modal pops without each caller wiring it.
      // Lazy-import to dodge the apiFetch ⟷ billingStore boot order.
      if (response.status === 403 && code === 'PLAN_LIMIT') {
        try {
          const detail = (err?.error?.details as Record<string, unknown> | undefined) || {};
          const { useBillingStore } = await import('../stores/billingStore');
          useBillingStore.getState().setPlanLimit({
            feature: typeof detail.feature === 'string' ? detail.feature : 'unknown',
            planId: typeof detail.planId === 'string' ? detail.planId : 'free',
            message,
            limit: typeof detail.limit === 'number' ? detail.limit : undefined,
            current: typeof detail.current === 'number' ? detail.current : undefined,
          });
        } catch {
          // Surfacing failures here can't be allowed to mask the original 403.
        }
      }

      throw new ApiError(message, response.status, code, err?.error?.details);
    }

    if (body && typeof body === 'object' && 'data' in body) {
      return (body as { data: T }).data;
    }

    return body as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError('Request timed out', 408, 'TIMEOUT');
    }
    throw new ApiError((err as Error).message, 0, 'NETWORK');
  }
}
